import React, { useState, useEffect, useRef } from 'react';
import { X, User, UserPlus, Loader2, Search, ChevronDown, Plus, Clock, IndianRupee, FileText } from 'lucide-react';

interface Nurse {
  id: string | number;
  full_name?: string;
  name?: string;
  first_name?: string;
  monthly_salary?: string | number;
}

interface FormData {
  nurse_id:  string;   // set when primary is a nurse
  physio_id: string;   // set when primary is a physio
  monthly_wage: string;
  daily_wage: string;
  client_charge: string;
  in_time: string;
  out_time: string;
  hasAssistant: boolean;
  second_nurse_id:  string;  // set when secondary is a nurse
  second_physio_id: string;  // set when secondary is a physio
  second_monthly_wage: string;
  second_daily_wage: string;
  second_client_charge: string;
  second_in_time: string;
  second_out_time: string;
  second_shift_type: string;
}

interface TimePickerProps { value: string; onChange: (val: string) => void; }

const TimePicker12H: React.FC<TimePickerProps> = ({ value, onChange }) => {
  const parseTime = (val: string) => {
    if (!val) return { hour: '12', minute: '00', period: 'AM' };
    const [h, m] = val.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    let hour = h % 12;
    if (hour === 0) hour = 12;
    return { hour: hour.toString().padStart(2, '0'), minute: m ? m.toString().padStart(2, '0') : '00', period };
  };
  const { hour, minute, period } = parseTime(value);
  const updateTime = (newHour: string, newMinute: string, newPeriod: string) => {
    let h = parseInt(newHour, 10);
    if (newPeriod === 'PM' && h !== 12) h += 12;
    if (newPeriod === 'AM' && h === 12) h = 0;
    onChange(`${h.toString().padStart(2, '0')}:${newMinute}`);
  };
  const hours = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));
  const selectStyle: React.CSSProperties = { flex: 1, background: 'transparent', border: 'none', padding: '0.25rem 0.5rem', fontSize: '0.875rem', fontWeight: 600, outline: 'none', appearance: 'none', textAlign: 'center', cursor: 'pointer', boxSizing: 'border-box' };
  return (
    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '0.5rem', backgroundColor: 'white', overflow: 'hidden', height: '2.5rem', boxSizing: 'border-box' }}>
      <select value={hour} onChange={(e) => updateTime(e.target.value, minute, period)} style={selectStyle}>
        {hours.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span style={{ color: '#cbd5e1', fontWeight: 800, fontSize: '0.875rem' }}>:</span>
      <select value={minute} onChange={(e) => updateTime(hour, e.target.value, period)} style={selectStyle}>
        {minutes.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
      <div style={{ position: 'relative', height: '100%', minWidth: '3.5rem' }}>
        <select value={period} onChange={(e) => updateTime(hour, minute, e.target.value)}
          style={{ backgroundColor: '#f1f5f9', border: 'none', borderLeft: '1px solid #e2e8f0', padding: '0 1rem 0 0.5rem', height: '100%', width: '100%', fontSize: '0.7rem', fontWeight: 900, color: '#475569', outline: 'none', cursor: 'pointer', boxSizing: 'border-box', appearance: 'none', textAlign: 'center', textTransform: 'uppercase' }}>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
        <ChevronDown size={10} style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
      </div>
    </div>
  );
};

// --- SEARCHABLE SELECT ---
interface SearchableNurseSelectProps { nurses: Nurse[]; value: string; onChange: (val: string) => void; onQuickAdd: () => void; staffType?: 'nurse' | 'physio'; }

const SearchableNurseSelect: React.FC<SearchableNurseSelectProps> = ({ nurses, value, onChange, onQuickAdd, staffType = 'nurse' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredNurses = nurses.filter(n => (n.full_name || n.name || n.first_name || '').toLowerCase().includes(search.toLowerCase()));
  const selectedNurse = nurses.find(n => n.id.toString() === value.toString());

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
      <div onClick={() => setIsOpen(!isOpen)} style={{ width: '100%', height: '2.5rem', padding: '0 0.75rem', backgroundColor: 'white', border: `1px solid ${isOpen ? '#3b82f6' : '#cbd5e1'}`, borderRadius: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: isOpen ? '0 0 0 2px rgba(59,130,246,0.1)' : 'none', boxSizing: 'border-box' }}>
        <span style={{ fontSize: '0.875rem', fontWeight: 500, color: selectedNurse ? '#0f172a' : '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedNurse ? (selectedNurse.full_name || selectedNurse.name || selectedNurse.first_name) : "Select staff..."}
        </span>
        <ChevronDown size={16} style={{ color: '#94a3b8', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
      </div>
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, marginTop: '0.5rem', backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '0.75rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', overflow: 'hidden', boxSizing: 'border-box' }}>
          <div style={{ padding: '0.5rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '0.375rem', padding: '0 0.5rem', border: '1px solid #e2e8f0', boxSizing: 'border-box' }}>
              <Search size={14} style={{ color: '#94a3b8' }} />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name..."
                style={{ width: '100%', padding: '0.375rem 0.5rem', fontSize: '0.875rem', outline: 'none', border: 'none', background: 'transparent', boxSizing: 'border-box' }} />
            </div>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredNurses.length > 0 ? filteredNurses.map(nurse => (
              <div key={nurse.id} onClick={() => { onChange(nurse.id.toString()); setIsOpen(false); setSearch(''); }}
                style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', color: '#334155', cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.color = '#1d4ed8'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.color = '#334155'; }}>
                {nurse.full_name || nurse.name || nurse.first_name}
              </div>
            )) : (
              <div style={{ padding: '0.75rem 1rem', fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No results found</div>
            )}
          </div>
          <button onClick={() => { setIsOpen(false); onQuickAdd(); }}
            style={{ width: '100%', padding: '0.75rem 1rem', border: 'none', borderTop: '1px solid #f1f5f9', backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 700, fontSize: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', cursor: 'pointer', boxSizing: 'border-box' }}>
            <Plus size={14} /> QUICK ADD NEW {staffType === 'physio' ? 'PHYSIO' : 'NURSE'}
          </button>
        </div>
      )}
    </div>
  );
};

// --- STAFF TYPE TOGGLE (compact inline segmented control) ---
const StaffTypeToggle: React.FC<{
  value: string;
  onChange: (type: string) => void;
}> = ({ value, onChange }) => (
  <div style={{ display: 'inline-flex', border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#f8fafc', marginBottom: '0.5rem' }}>
    {(['nurse', 'physio'] as const).map(type => {
      const isActive = value === type;
      const activeColor = type === 'physio' ? '#7c3aed' : '#2563eb';
      return (
        <button key={type}
          onClick={() => onChange(type)}
          style={{
            padding: '0.2rem 0.75rem',
            fontSize: '0.65rem', fontWeight: 900, letterSpacing: '0.06em',
            textTransform: 'uppercase', border: 'none', cursor: 'pointer',
            backgroundColor: isActive ? activeColor : 'transparent',
            color: isActive ? 'white' : '#64748b',
            transition: 'all 0.15s ease',
          }}>
          {type}
        </button>
      );
    })}
  </div>
);

interface ShiftEditDrawerProps {
  selectedSlots: any[];
  nurses: Nurse[];
  physios?: Nurse[];
  dutyType?: string;
  scheduleMap: Record<string, any>;
  onClose: () => void;
  onSave: (data: FormData) => void;
  isSaving: boolean;
  onQuickAdd: () => void;
  onDelete: () => void;
  preNurseId?: string;
}

const getDefaultTimes = (shiftType: string) => {
  const lower = shiftType.toLowerCase();
  if (lower.includes('08 morning') || lower.includes('8 morning')) return { in_time: '06:00', out_time: '14:00' };
  if (lower.includes('08 afternoon') || lower.includes('8 afternoon')) return { in_time: '14:00', out_time: '22:00' };
  if (lower.includes('08 night') || lower.includes('8 night')) return { in_time: '22:00', out_time: '06:00' };
  if (lower.includes('12 day')) return { in_time: '08:00', out_time: '20:00' };
  if (lower.includes('12 night')) return { in_time: '20:00', out_time: '08:00' };
  if (lower.includes('24')) return { in_time: '08:00', out_time: '08:00' };
  return { in_time: '08:00', out_time: '20:00' };
};

const calculateHoursDifference = (inTime: string, outTime: string): number => {
  if (!inTime || !outTime) return 0;
  const [inH, inM] = inTime.split(':').map(Number);
  const [outH, outM] = outTime.split(':').map(Number);
  let diff = outH + outM / 60 - (inH + inM / 60);
  if (diff < 0) diff += 24;
  if (diff === 0) diff = 24;
  return Math.round(diff * 100) / 100;
};

const ShiftEditDrawer: React.FC<ShiftEditDrawerProps> = ({
  selectedSlots, nurses, physios = [], dutyType, scheduleMap,
  onClose, onSave, isSaving, onQuickAdd, onDelete, preNurseId,
}) => {
  const isPhysioCase = dutyType === 'Physiotherapy';
  const defaultStaffType = isPhysioCase ? 'physio' : 'nurse';
  const [formData, setFormData] = useState<FormData>({
    nurse_id: '', physio_id: '',
    monthly_wage: '', daily_wage: '', client_charge: '', in_time: '08:00', out_time: '20:00',
    hasAssistant: false,
    second_nurse_id: '', second_physio_id: '',
    second_monthly_wage: '', second_daily_wage: '', second_client_charge: '',
    second_in_time: '08:00', second_out_time: '20:00', second_shift_type: '12 Day',
  });

  // UI-only toggles — not part of the payload; they control which parallel column is active
  const [staffTypeToggle, setStaffTypeToggle] = useState<'nurse' | 'physio'>(defaultStaffType);
  const [secondStaffTypeToggle, setSecondStaffTypeToggle] = useState<'nurse' | 'physio'>(defaultStaffType);

  const handleTimeChange = (field: 'in_time' | 'out_time' | 'second_in_time' | 'second_out_time', val: string) => {
    setFormData(prev => ({ ...prev, [field]: val }));
  };

  // Returns the list currently shown for primary or secondary selection
  const getStaffList = (type: string) => type === 'physio' ? physios : nurses;

  // Keep a ref to the latest scheduleMap so the form-load effect can read it
  // WITHOUT re-running every time scheduleMap is updated (which would reset mid-edit forms).
  const scheduleMapRef = useRef(scheduleMap);
  useEffect(() => { scheduleMapRef.current = scheduleMap; });

  // Track which slot key the form was last loaded for — only reload when the selection changes.
  const loadedSlotKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedSlots.length === 0) { loadedSlotKeyRef.current = null; return; }

    const firstSlot = selectedSlots[0];
    const slotKey = `${firstSlot.date}_${firstSlot.session}_${selectedSlots.length}`;

    // Don't reset the form if this is the same selection (e.g. scheduleMap refreshed mid-edit)
    if (slotKey === loadedSlotKeyRef.current) return;
    loadedSlotKeyRef.current = slotKey;

    // Load from the first slot that actually has a staff member assigned — so that
    // a mixed selection (filled + empty days) still shows the existing primary.
    // Falling back to selectedSlots[0] when nothing is assigned.
    const representativeSlot = selectedSlots.find(s => {
      const c = scheduleMapRef.current[s.date]?.[s.session];
      return c && (c.nurse_id || c.physio_id) && !c.is_no_duty;
    }) || firstSlot;

    const existingData = scheduleMapRef.current[representativeSlot.date]?.[representativeSlot.session];
    const defaultTimes = getDefaultTimes(representativeSlot.session);

    if (existingData) {
      const monthlyWage = existingData.monthly_scale
        ? existingData.monthly_scale.toString()
        : (existingData.rate ? Math.round(existingData.rate * 30).toString() : '');
      const secondMonthlyWage = existingData.second_monthly_scale
        ? existingData.second_monthly_scale.toString()
        : (existingData.second_rate ? Math.round(existingData.second_rate * 30).toString() : '');

      if (existingData.physio_id) setStaffTypeToggle('physio');
      else if (existingData.nurse_id) setStaffTypeToggle('nurse');
      else setStaffTypeToggle(defaultStaffType);
      if (existingData.second_physio_id) setSecondStaffTypeToggle('physio');
      else if (existingData.second_nurse_id) setSecondStaffTypeToggle('nurse');
      else setSecondStaffTypeToggle(defaultStaffType);

      setFormData({
        nurse_id:  existingData.nurse_id?.toString()  || '',
        physio_id: existingData.physio_id?.toString() || '',
        monthly_wage: monthlyWage,
        daily_wage: existingData.rate?.toString() || '',
        client_charge: existingData.total_charge?.toString() || '',
        in_time: existingData.in_time || defaultTimes.in_time,
        out_time: existingData.out_time || defaultTimes.out_time,
        hasAssistant: existingData.hasAssistant || false,
        second_nurse_id:  existingData.second_nurse_id?.toString()  || '',
        second_physio_id: existingData.second_physio_id?.toString() || '',
        second_monthly_wage: secondMonthlyWage,
        second_daily_wage: existingData.second_rate?.toString() || '',
        second_client_charge: existingData.second_total_charge?.toString() || '',
        second_in_time: existingData.second_in_time || (existingData.second_shift_type ? getDefaultTimes(existingData.second_shift_type).in_time : defaultTimes.in_time),
        second_out_time: existingData.second_out_time || (existingData.second_shift_type ? getDefaultTimes(existingData.second_shift_type).out_time : defaultTimes.out_time),
        second_shift_type: existingData.second_shift_type || '12 Day',
      });
    } else {
      setStaffTypeToggle(defaultStaffType);
      setSecondStaffTypeToggle(defaultStaffType);
      setFormData({
        nurse_id: preNurseId || '', physio_id: '',
        monthly_wage: '', daily_wage: '', client_charge: '',
        in_time: defaultTimes.in_time, out_time: defaultTimes.out_time,
        hasAssistant: false,
        second_nurse_id: '', second_physio_id: '',
        second_monthly_wage: '', second_daily_wage: '', second_client_charge: '',
        second_in_time: '08:00', second_out_time: '20:00', second_shift_type: '12 Day',
      });
    }
  }, [selectedSlots, preNurseId]); // scheduleMap intentionally excluded — use ref to avoid mid-edit resets

  const handleStaffSelect = (val: string, isSecondary: boolean = false) => {
    const activeType = isSecondary ? secondStaffTypeToggle : staffTypeToggle;
    const list = getStaffList(activeType);
    const selected = list.find(n => n.id.toString() === val);
    const monthly = selected?.monthly_salary ? parseFloat(selected.monthly_salary.toString()) : 0;
    const daily = monthly ? Math.round(monthly / 30).toString() : '';

    if (isSecondary) {
      setFormData(prev => ({
        ...prev,
        second_nurse_id:  activeType === 'nurse'  ? val : '',
        second_physio_id: activeType === 'physio' ? val : '',
        second_monthly_wage: monthly ? monthly.toString() : '',
        second_daily_wage: daily,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        nurse_id:  activeType === 'nurse'  ? val : '',
        physio_id: activeType === 'physio' ? val : '',
        monthly_wage: monthly ? monthly.toString() : '',
        daily_wage: daily,
      }));
    }
  };

  const handleStaffTypeChange = (type: string, isSecondary: boolean) => {
    if (isSecondary) {
      setSecondStaffTypeToggle(type as 'nurse' | 'physio');
      setFormData(prev => ({ ...prev, second_nurse_id: '', second_physio_id: '', second_monthly_wage: '', second_daily_wage: '' }));
    } else {
      setStaffTypeToggle(type as 'nurse' | 'physio');
      setFormData(prev => ({ ...prev, nurse_id: '', physio_id: '', monthly_wage: '', daily_wage: '' }));
    }
  };

  const handleMonthlyWageChange = (val: string, isSecondary: boolean = false) => {
    const monthly = parseFloat(val);
    const daily = !isNaN(monthly) ? Math.round(monthly / 30).toString() : '';
    if (isSecondary) setFormData(prev => ({ ...prev, second_monthly_wage: val, second_daily_wage: daily }));
    else setFormData(prev => ({ ...prev, monthly_wage: val, daily_wage: daily }));
  };

  const handleDailyWageChange = (val: string, isSecondary: boolean = false) => {
    const daily = parseFloat(val);
    const monthly = !isNaN(daily) ? Math.round(daily * 30).toString() : '';
    if (isSecondary) setFormData(prev => ({ ...prev, second_daily_wage: val, second_monthly_wage: monthly }));
    else setFormData(prev => ({ ...prev, daily_wage: val, monthly_wage: monthly }));
  };

  if (selectedSlots.length === 0) return null;

  const activePrimaryId = staffTypeToggle === 'physio' ? formData.physio_id : formData.nurse_id;
  const activeSecondaryId = secondStaffTypeToggle === 'physio' ? formData.second_physio_id : formData.second_nurse_id;

  const handleSaveClick = () => {
    if (!activePrimaryId) { alert("Please select a primary staff member."); return; }
    if (formData.hasAssistant && !activeSecondaryId) { alert("Please select the second staff member."); return; }
    onSave(formData);
  };

  const buildSalarySheetUrl = (type: string, id: string) => {
    const d = new Date(selectedSlots[0]?.date || Date.now());
    return `/salarysheet/${type}/${id}?month=${d.getMonth() + 1}&year=${d.getFullYear()}`;
  };

  const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
    <label style={{ display: 'block', fontSize: '10px', fontWeight: 900, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.375rem', marginLeft: '0.125rem', boxSizing: 'border-box' }}>
      {children} {required && <span style={{ color: '#ef4444' }}>*</span>}
    </label>
  );

  const inputBaseStyle: React.CSSProperties = {
    width: '100%', height: '2.5rem', padding: '0 0.75rem', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 600, color: '#334155', outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box'
  };

  return (
    <div style={{ position: 'fixed', top: 0, bottom: 0, right: 0, width: '520px', backgroundColor: 'white', boxShadow: '-20px 0 50px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 50, borderLeft: '1px solid #e2e8f0', boxSizing: 'border-box' }}>

      {/* Header */}
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(248,250,252,0.5)', boxSizing: 'border-box' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.25 }}>Shift Assignment</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem' }}>
            <span style={{ padding: '0.125rem 0.5rem', backgroundColor: '#dbeafe', color: '#1d4ed8', fontSize: '10px', fontWeight: 900, borderRadius: '9999px', textTransform: 'uppercase', letterSpacing: '-0.025em' }}>
              {selectedSlots.length} Slots Selected
            </span>
          </div>
        </div>
        <button onClick={onClose} style={{ padding: '0.5rem', backgroundColor: 'transparent', border: 'none', borderRadius: '9999px', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}>
          <X size={20} />
        </button>
      </div>

      {/* Body */}
      <div className="custom-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '2rem', boxSizing: 'border-box' }}>

        {/* ——————————————————————————————————
            PRIMARY STAFF SECTION
        —————————————————————————————————— */}
        <section style={{ position: 'relative', paddingLeft: '1rem', borderLeft: '4px solid #3b82f6', display: 'flex', flexDirection: 'column', gap: '1.25rem', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <div style={{ padding: '0.375rem', backgroundColor: '#eff6ff', borderRadius: '0.5rem' }}><User size={18} style={{ color: '#2563eb' }} /></div>
            <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 900, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Primary Staff</h4>
          </div>

          <div>
            {/* Toggle only shown when duty type is unknown; otherwise type is inferred from dutyType */}
            {!dutyType && <StaffTypeToggle value={staffTypeToggle} onChange={type => handleStaffTypeChange(type, false)} />}
            <Label required>Select {staffTypeToggle === 'physio' ? 'Physio' : 'Nurse'}</Label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <SearchableNurseSelect
                  nurses={getStaffList(staffTypeToggle)}
                  value={activePrimaryId}
                  onChange={val => handleStaffSelect(val, false)}
                  onQuickAdd={onQuickAdd}
                  staffType={staffTypeToggle}
                />
              </div>
              {activePrimaryId && (
                <button
                  onClick={() => window.open(buildSalarySheetUrl(staffTypeToggle, activePrimaryId), '_blank')}
                  style={{ padding: '0 0.75rem', height: '2.5rem', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                >
                  <FileText size={14} style={{ marginRight: '4px' }} /> Salary Sheet
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', boxSizing: 'border-box', width: '100%' }}>
            <div style={{ minWidth: 0 }}>
              <Label>Monthly</Label>
              <div style={{ position: 'relative', width: '100%' }}>
                <input style={{ ...inputBaseStyle, paddingLeft: '1.75rem' }} type="number" value={formData.monthly_wage} onChange={e => handleMonthlyWageChange(e.target.value, false)} placeholder="0" />
                <IndianRupee size={12} style={{ position: 'absolute', left: '0.625rem', top: '0.875rem', color: '#94a3b8' }} />
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <Label>Daily</Label>
              <input style={inputBaseStyle} type="number" value={formData.daily_wage} onChange={e => handleDailyWageChange(e.target.value, false)} placeholder="0" />
            </div>
            <div style={{ minWidth: 0 }}>
              <Label>Charge</Label>
              <input style={inputBaseStyle} type="number" value={formData.client_charge} onChange={e => setFormData({ ...formData, client_charge: e.target.value })} placeholder="0" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', paddingTop: '0.25rem', boxSizing: 'border-box' }}>
            <div>
              <Label>In Time</Label>
              <TimePicker12H value={formData.in_time} onChange={val => handleTimeChange('in_time', val)} />
            </div>
            <div>
              <Label>Out Time <span style={{ color: '#3b82f6', marginLeft: '6px', fontSize: '10px' }}>({calculateHoursDifference(formData.in_time, formData.out_time)} hrs)</span></Label>
              <TimePicker12H value={formData.out_time} onChange={val => handleTimeChange('out_time', val)} />
            </div>
          </div>
        </section>

        {/* ——————————————————————————————————
            SECOND STAFF SECTION
        —————————————————————————————————— */}
        <section style={{ position: 'relative', paddingLeft: '1rem', borderLeft: `4px solid ${formData.hasAssistant ? '#6366f1' : '#e2e8f0'}`, display: 'flex', flexDirection: 'column', gap: '1.25rem', transition: 'border-color 0.3s ease', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ padding: '0.375rem', backgroundColor: formData.hasAssistant ? '#f5f3ff' : '#f1f5f9', borderRadius: '0.5rem', transition: 'background-color 0.3s ease' }}>
                <UserPlus size={18} style={{ color: formData.hasAssistant ? '#4f46e5' : '#94a3b8' }} />
              </div>
              <h4 style={{ margin: 0, fontSize: '12px', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em', color: formData.hasAssistant ? '#312e81' : '#94a3b8', transition: 'color 0.3s ease' }}>Add Second Staff</h4>
            </div>
            <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
              <input type="checkbox" style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} checked={formData.hasAssistant} onChange={e => setFormData({ ...formData, hasAssistant: e.target.checked })} />
              <div style={{ width: '2.75rem', height: '1.5rem', backgroundColor: formData.hasAssistant ? '#4f46e5' : '#e2e8f0', borderRadius: '9999px', position: 'relative', transition: 'background-color 0.3s ease' }}>
                <div style={{ content: '""', position: 'absolute', top: '2px', left: formData.hasAssistant ? 'calc(100% - 22px)' : '2px', width: '20px', height: '20px', backgroundColor: 'white', borderRadius: '50%', transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }} />
              </div>
            </label>
          </div>

          {formData.hasAssistant && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', boxSizing: 'border-box' }}>
              <div>
                {/* Toggle only shown when duty type is unknown */}
                {!dutyType && <StaffTypeToggle value={secondStaffTypeToggle} onChange={type => handleStaffTypeChange(type, true)} />}
                <Label required>Select Second {secondStaffTypeToggle === 'physio' ? 'Physio' : 'Nurse'}</Label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <SearchableNurseSelect
                      nurses={getStaffList(secondStaffTypeToggle)}
                      value={activeSecondaryId}
                      onChange={val => handleStaffSelect(val, true)}
                      onQuickAdd={onQuickAdd}
                      staffType={secondStaffTypeToggle}
                    />
                  </div>
                  {activeSecondaryId && (
                    <button
                      onClick={() => window.open(buildSalarySheetUrl(secondStaffTypeToggle, activeSecondaryId), '_blank')}
                      style={{ padding: '0 0.75rem', height: '2.5rem', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', borderRadius: '0.5rem', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                    >
                      <FileText size={14} style={{ marginRight: '4px' }} /> Salary Sheet
                    </button>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', boxSizing: 'border-box', width: '100%' }}>
                <div style={{ minWidth: 0 }}>
                  <Label>Monthly</Label>
                  <input style={inputBaseStyle} type="number" value={formData.second_monthly_wage} onChange={e => handleMonthlyWageChange(e.target.value, true)} placeholder="0" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Label>Daily</Label>
                  <input style={inputBaseStyle} type="number" value={formData.second_daily_wage} onChange={e => handleDailyWageChange(e.target.value, true)} placeholder="0" />
                </div>
                <div style={{ minWidth: 0 }}>
                  <Label>Charge</Label>
                  <input style={inputBaseStyle} type="number" value={formData.second_client_charge} onChange={e => setFormData({ ...formData, second_client_charge: e.target.value })} placeholder="0" />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', paddingTop: '0.25rem', boxSizing: 'border-box' }}>
                <div>
                  <Label>In Time</Label>
                  <TimePicker12H value={formData.second_in_time} onChange={val => handleTimeChange('second_in_time', val)} />
                </div>
                <div>
                  <Label>Out Time <span style={{ color: '#3b82f6', marginLeft: '6px', fontSize: '10px' }}>({calculateHoursDifference(formData.second_in_time, formData.second_out_time)} hrs)</span></Label>
                  <TimePicker12H value={formData.second_out_time} onChange={val => handleTimeChange('second_out_time', val)} />
                </div>
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Footer */}
      <div style={{ padding: '1.5rem', borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc', display: 'flex', gap: '0.75rem', boxSizing: 'border-box' }}>
        <button style={{ flex: 1, padding: '0.75rem', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '0.75rem', fontWeight: 800, color: '#ef4444', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.025em', cursor: isSaving ? 'not-allowed' : 'pointer', boxSizing: 'border-box' }}
          onClick={onDelete} disabled={isSaving}
          onMouseEnter={e => { if (!isSaving) e.currentTarget.style.backgroundColor = '#fecaca'; }}
          onMouseLeave={e => { if (!isSaving) e.currentTarget.style.backgroundColor = '#fee2e2'; }}>
          Delete
        </button>
        <button style={{ flex: 1, padding: '0.75rem', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '0.75rem', fontWeight: 800, color: '#475569', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.025em', cursor: 'pointer', boxSizing: 'border-box' }}
          onClick={onClose}
          onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f1f5f9'; e.currentTarget.style.borderColor = '#94a3b8'; }}
          onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'white'; e.currentTarget.style.borderColor = '#cbd5e1'; }}>
          Cancel
        </button>
        <button style={{ flex: 2, padding: '0.75rem', backgroundColor: isSaving ? '#93c5fd' : '#2563eb', border: 'none', borderRadius: '0.75rem', fontWeight: 800, color: 'white', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.025em', cursor: isSaving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', boxShadow: '0 10px 15px -3px rgba(37,99,235,0.2)', boxSizing: 'border-box' }}
          onClick={handleSaveClick} disabled={isSaving}
          onMouseEnter={e => { if (!isSaving) e.currentTarget.style.backgroundColor = '#1d4ed8'; }}
          onMouseLeave={e => { if (!isSaving) e.currentTarget.style.backgroundColor = '#2563eb'; }}>
          {isSaving
            ? (<><Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /><span>Processing...</span></>)
            : (<><span>Save Assignments</span><Clock size={16} style={{ opacity: 0.8 }} /></>)}
        </button>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .custom-scrollbar::-webkit-scrollbar { width: 5px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }`}</style>
    </div>
  );
};

export default ShiftEditDrawer;
